import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Image as ImageIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import MediaPicker from "@/components/MediaPicker";
import { PAGE_SCHEMAS } from "./pageSchemas";

function ImageField({ field, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="eyebrow text-ink/55">{field.label}</label>
      <div className="mt-3 flex items-start gap-4">
        <div className="w-32 h-32 bg-mist/40 border border-mist flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={mediaUrl(value)} alt={field.label} className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={22} className="text-ink/30" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid={`page-field-${field.name}-pick`}
              className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper"
            >
              Choose from library
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange(field.name, "")}
                className="text-[12px] tracking-[0.14em] uppercase text-ink/50 hover:text-copper inline-flex items-center gap-1"
                data-testid={`page-field-${field.name}-clear`}
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>
          <input
            type="text"
            data-testid={`page-field-${field.name}`}
            value={value || ""}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder="Or paste an image URL (e.g. /api/uploads/… or https://…)"
            className="input-line text-[13px]"
          />
          {field.help && <p className="text-[12px] text-ink/50 italic">{field.help}</p>}
        </div>
      </div>
      <MediaPicker
        open={open}
        onClose={() => setOpen(false)}
        onPick={(url) => onChange(field.name, url)}
      />
    </div>
  );
}

function Field({ field, value, onChange }) {
  if (field.type === "image") {
    return <ImageField field={field} value={value} onChange={onChange} />;
  }
  if (field.type === "textarea") {
    return (
      <div>
        <label className="eyebrow text-ink/55">{field.label}</label>
        <textarea
          data-testid={`page-field-${field.name}`}
          value={value ?? ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          rows={3}
          className="input-line mt-3 resize-y"
        />
      </div>
    );
  }
  return (
    <div>
      <label className="eyebrow text-ink/55">{field.label}</label>
      <input
        data-testid={`page-field-${field.name}`}
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        className="input-line mt-3"
      />
    </div>
  );
}

export default function PageEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const schema = PAGE_SCHEMAS[slug];
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/pages/${slug}`).then(r => setPage(r.data)).catch(() => {
      toast.error("Page not found");
      navigate("/studio/pages", { replace: true });
    });
  }, [slug, navigate]);

  if (!schema) return <p>Unknown page</p>;
  if (!page) return <p className="text-ink/50 text-sm">Loading…</p>;

  const content = page.content || {};
  const seo = page.seo || {};

  const setContent = (k, v) => setPage({ ...page, content: { ...content, [k]: v } });
  const setSeo = (k, v) => setPage({ ...page, seo: { ...seo, [k]: v } });
  const setStatus = (s) => setPage({ ...page, status: s });

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const r = await api.put(`/admin/pages/${slug}`, {
        status: page.status,
        content: page.content,
        seo: page.seo,
      });
      setPage(r.data);
      toast.success(`${schema.label} saved.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`page-edit-${slug}`}>
      <Link to="/studio/pages" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ink/60 hover:text-copper">
        <ChevronLeft size={14} /> Back to pages
      </Link>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">{schema.label}</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[12px] tracking-[0.14em] uppercase text-ink/55">Status</label>
          <select
            data-testid="page-status-select"
            value={page.status || "draft"}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-mist px-3 py-2 text-[13px] bg-ivory"
          >
            <option value="published">Published</option>
            <option value="draft">Draft (hidden from public)</option>
          </select>
        </div>
      </div>

      <form onSubmit={save} className="mt-12 space-y-14">
        {schema.sections.map((section) => (
          <section key={section.title} className="space-y-6">
            <p className="eyebrow text-copper">{section.title}</p>
            <div className="space-y-6">
              {section.fields.map((f) => (
                <Field
                  key={f.name}
                  field={f}
                  value={content[f.name]}
                  onChange={setContent}
                />
              ))}
            </div>
          </section>
        ))}

        {/* SEO */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">SEO &amp; social</p>
          <div>
            <label className="eyebrow text-ink/55">Page title (browser tab + search results)</label>
            <input data-testid="seo-title" type="text" value={seo.title ?? ""} onChange={(e) => setSeo("title", e.target.value)} className="input-line mt-3" />
          </div>
          <div>
            <label className="eyebrow text-ink/55">Meta description</label>
            <textarea data-testid="seo-description" value={seo.description ?? ""} onChange={(e) => setSeo("description", e.target.value)} rows={2} className="input-line mt-3 resize-y" />
          </div>
          <div>
            <label className="eyebrow text-ink/55">Open Graph title (Facebook, LinkedIn, etc.)</label>
            <input data-testid="seo-og-title" type="text" value={seo.og_title ?? ""} onChange={(e) => setSeo("og_title", e.target.value)} className="input-line mt-3" />
          </div>
          <div>
            <label className="eyebrow text-ink/55">Open Graph description</label>
            <textarea data-testid="seo-og-description" value={seo.og_description ?? ""} onChange={(e) => setSeo("og_description", e.target.value)} rows={2} className="input-line mt-3 resize-y" />
          </div>
        </section>

        <div className="pt-4 flex justify-end gap-3">
          <Link to="/studio/pages" className="btn-secondary">Cancel</Link>
          <button data-testid="page-save" disabled={saving} type="submit" className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
